import React, { useMemo, useState } from 'react';
import { Plus, Calculator as CalcIcon, Coins, Calendar as CalendarIcon, Database, FileText, BookOpen, Target, ArrowRight, TrendingUp, Flame } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

// Robust date normalizer: ISO, M/D/YYYY, D/M/YYYY (auto-swap), 2-digit years, time suffixes
export const normalizeDateKey = (dateStr) => {
  if (!dateStr) return null;
  const s = String(dateStr).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
  const parts = s.split(' ')[0].split(/[/.-]/);
  if (parts.length < 3) return null;
  let a = parseInt(parts[0], 10), b = parseInt(parts[1], 10), y = parseInt(parts[2], 10);
  if (![a, b, y].every(Number.isFinite)) return null;
  if (y < 100) y += 2000;
  let month = a, day = b;
  if (month > 12 && day <= 12) { month = b; day = a; }
  if (month > 12 || month < 1 || day > 31 || day < 1) return null;
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
import { formatNumber } from '../utils';
import { buildDayGroups, computeExecutedRows } from '../utils/planLogic';

function HomeCard({ onClick, icon, title, description, variant = 'default' }) {
  return (
    <button 
      onClick={onClick}
      data-testid={`home-card-${title.toLowerCase().replace(/\s+/g, '-')}`}
      className={`bg-white p-4 sm:p-5 rounded-xl shadow-sm border ${variant === 'admin' ? 'border-blue-200 bg-blue-50/30' : 'border-gray-100'} hover:shadow-md hover:border-blue-200 transition-all text-left flex items-start gap-3 sm:gap-4 group w-full h-full`}
    >
      <div className={`p-2.5 sm:p-3 ${variant === 'admin' ? 'bg-blue-100' : 'bg-gray-50'} rounded-lg group-hover:bg-blue-50 transition-colors shrink-0`}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className={`font-bold text-sm sm:text-base ${variant === 'admin' ? 'text-blue-900' : 'text-gray-900'} flex items-center justify-between`}>
          <span className="truncate">{title}</span>
          <ArrowRight size={16} className="text-gray-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all shrink-0 ml-2" />
        </h3>
        <p className="text-xs sm:text-sm text-gray-500 leading-tight mt-1 line-clamp-2">{description}</p>
      </div>
    </button>
  );
}

function ProgressChart({ bitacoraEntries, estudioEntries, percentage, initialCapital, totalDeposits, confirmedExtractions, accountAdjustment = 0 }) {
  const { language } = useLanguage();
  const [filter, setFilter] = useState('total');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3)); // total, monthly, quarterly, yearly
  
  const chartData = useMemo(() => {
    // One point per trading day from shared plan logic: the curve shows TOTAL CUENTA
    const rows = computeExecutedRows({
      dayGroups: buildDayGroups(bitacoraEntries, estudioEntries),
      percentage,
      initialCapital,
      confirmedExtractions,
      startingBalance: initialCapital + (totalDeposits || 0) + (accountAdjustment || 0)
    });

    if (rows.length === 0) return { points: [], hasData: false };

    const dailyData = {};
    rows.forEach((row) => {
      const dateKey = normalizeDateKey(row.date);
      if (!dateKey) return;
      // Total Cuenta curve: full real profit (incl. Profit+) minus losses and confirmed extractions.
      dailyData[dateKey] = {
        profit: row.accountDelta,
        trades: row.opsCount
      };
    });

    if (Object.keys(dailyData).length === 0) return { points: [], hasData: false };

    const sortedDates = Object.keys(dailyData).sort();
    const monthNames = language === 'es' 
      ? ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']
      : ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Generate CUMULATIVE points from start date to end date
    const actualToday = new Date();  // Current real date
    let today = new Date();  // End date for chart (may be adjusted)
    // The curve starts at the account base: initial capital + extra deposits + manual adjustment
    let currentBalance = (initialCapital || 0) + (totalDeposits || 0) + (accountAdjustment || 0);
    
    // Determine start date based on filter
    let startDate;
    if (filter === 'monthly') {
      // Show specific month selected
      startDate = new Date(selectedYear, selectedMonth, 1);
      const endOfMonth = new Date(selectedYear, selectedMonth + 1, 0);
      today = endOfMonth < actualToday ? endOfMonth : actualToday;  // Don't go beyond today
    } else if (filter === 'quarterly') {
      // Show specific quarter selected
      const quarterStartMonth = (selectedQuarter - 1) * 3;
      startDate = new Date(selectedYear, quarterStartMonth, 1);
      const quarterEndMonth = quarterStartMonth + 2;
      const endOfQuarter = new Date(selectedYear, quarterEndMonth + 1, 0);
      today = endOfQuarter < actualToday ? endOfQuarter : actualToday;  // Don't go beyond today
    } else if (filter === 'yearly') {
      // Show specific year selected
      startDate = new Date(selectedYear, 0, 1);
      const endOfYear = new Date(selectedYear, 11, 31);
      today = endOfYear < actualToday ? endOfYear : actualToday;  // Don't go beyond today
    } else {
      // 'total' - start from first trade date to today (current date)
      const firstDate = new Date(sortedDates[0]);
      startDate = firstDate;
    }
    
    // If start date is in the future, show empty chart with message
    if (startDate > actualToday) {
      return [];  // Return empty array, will show "Sin datos" message
    }
    
    const points = [];
    
    // Pre-accumulate trading days BEFORE the selected period so the curve starts at the right balance
    const startKey = startDate.toISOString().split('T')[0];
    sortedDates.forEach((k) => {
      if (k < startKey) currentBalance += dailyData[k].profit;
    });
    
    // Iterate through each day from startDate to today
    for (let d = new Date(startDate); d <= today; d.setDate(d.getDate() + 1)) {
      const dateKey = d.toISOString().split('T')[0];
      
      // Add this day's planned profit / full loss (TOTAL CUENTA curve)
      if (dailyData[dateKey]) {
        currentBalance += dailyData[dateKey].profit;
      }
      
      // Format label
      const [year, month, day] = dateKey.split('-');
      const label = `${monthNames[parseInt(month) - 1]} ${day}`;
      
      points.push({
        label,
        fullLabel: `${day}/${month}`,
        value: currentBalance,
        date: dateKey,
        dayProfit: dailyData[dateKey]?.profit || 0
      });
    }
    
    // Sample points to avoid overcrowding (show max 40 points)
    let displayPoints = points;
    if (points.length > 40) {
      const step = Math.ceil(points.length / 40);
      displayPoints = points.filter((_, i) => i % step === 0 || i === points.length - 1);
    }
    
    // Calculate today's change (compare to yesterday's balance)
    const todayKey = today.toISOString().split('T')[0];
    const todayChange = dailyData[todayKey]?.profit || 0;
    
    // Find yesterday's balance (last point before today)
    const yesterdayBalance = points.length >= 2 ? points[points.length - 2].value : 0;
    const todayChangePercent = yesterdayBalance !== 0 ? ((todayChange / Math.abs(yesterdayBalance)) * 100) : 0;
    
    // Calculate ranges for Y axis (fit around the account balance curve)
    const values = points.map(p => p.value);
    const rawMax = Math.max(...values);
    const rawMin = Math.min(...values);
    const pad = (rawMax - rawMin) * 0.15 || Math.abs(rawMax) * 0.1 || 100;

    return { 
      points: displayPoints,
      hasData: points.length > 0, 
      currentBalance,
      todayChange,
      todayChangePercent,
      maxValue: rawMax + pad,
      minValue: rawMin - pad,
    };
  }, [bitacoraEntries, estudioEntries, filter, language, selectedMonth, selectedYear, selectedQuarter, percentage, initialCapital, totalDeposits, confirmedExtractions, accountAdjustment]);

  if (!chartData.hasData) {
    return (
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 text-center">
        <TrendingUp className="mx-auto text-gray-300 mb-2" size={40} />
        <p className="text-gray-400 text-sm">
          {language === 'es' ? 'No hay datos para mostrar el gráfico' : 'No data to display chart'}
        </p>
      </div>
    );
  }

  const maxValue = chartData.maxValue;
  const minValue = chartData.minValue;
  const range = maxValue - minValue || 1;
  const chartHeight = 200;

  // Calculate SVG path for line chart
  const getY = (value) => {
    return chartHeight - ((value - minValue) / range) * chartHeight;
  };

  const pathData = chartData.points.map((point, index) => {
    const x = (index / (chartData.points.length - 1 || 1)) * 100;
    const y = getY(point.value);
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  const areaPath = pathData + ` L 100 ${chartHeight} L 0 ${chartHeight} Z`;

  return (
    <div className="bg-white p-4 sm:p-6 rounded-xl shadow-sm border border-gray-100">
      {/* Current Balance and Today's Change */}
      <div className="mb-6">
        <p className="text-[10px] sm:text-xs font-bold text-emerald-600 uppercase tracking-wide mb-1" data-testid="chart-total-account-label">
          {language === 'es' ? 'Total Cuenta' : 'Total Account'}
        </p>
        <div className="text-3xl sm:text-4xl font-bold text-gray-900 mb-2" data-testid="chart-total-account-value">
          ${formatNumber(chartData.currentBalance)}
        </div>
        <div className={`flex items-center gap-2 text-sm ${
          chartData.todayChange >= 0 ? 'text-emerald-600' : 'text-red-600'
        }`}>
          {chartData.todayChange >= 0 ? '▲' : '▼'}
          <span className="font-semibold">
            ${Math.abs(chartData.todayChange).toFixed(2)} ({chartData.todayChangePercent >= 0 ? '+' : ''}{chartData.todayChangePercent.toFixed(2)}%)
          </span>
          <span className="text-gray-500">
            {language === 'es' ? 'Ganancia/Pérdida de Hoy' : 'Today\'s Gain/Loss'}
          </span>
        </div>
      </div>

      {/* Filter buttons and Period Selectors */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        {/* Main filter buttons */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-lg">
          {[
            { key: 'total', label: language === 'es' ? 'Todo' : 'All' },
            { key: 'monthly', label: language === 'es' ? 'Mes' : 'Month' },
            { key: 'quarterly', label: language === 'es' ? 'Trimestre' : 'Quarter' },
            { key: 'yearly', label: language === 'es' ? 'Año' : 'Year' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                filter === f.key 
                  ? 'bg-emerald-500 text-white shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Month selector - show when filter is 'monthly' */}
        {filter === 'monthly' && (
          <select 
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
            className="px-3 py-1.5 text-xs sm:text-sm font-medium bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            {(language === 'es' 
              ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
              : ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
            ).map((month, index) => (
              <option key={month} value={index}>{month}</option>
            ))}
          </select>
        )}

        {/* Quarter selector - show when filter is 'quarterly' */}
        {filter === 'quarterly' && (
          <select 
            value={selectedQuarter}
            onChange={(e) => setSelectedQuarter(parseInt(e.target.value))}
            className="px-3 py-1.5 text-xs sm:text-sm font-medium bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            {[1, 2, 3, 4].map(q => (
              <option key={q} value={q}>
                {language === 'es' ? `Trimestre ${q}` : `Q${q}`}
              </option>
            ))}
          </select>
        )}

        {/* Year selector - show when filter is NOT 'total' */}
        {filter !== 'total' && (
          <select 
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
            className="px-3 py-1.5 text-xs sm:text-sm font-medium bg-white border border-gray-300 rounded-md focus:ring-2 focus:ring-emerald-500 outline-none"
          >
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        )}
      </div>
      
      {/* Chart container with Y-axis space */}
      <div className="pl-10 sm:pl-12">
        {/* Line Chart */}
        <div className="relative h-48 sm:h-56 mt-4">
        {/* Y-axis labels and grid lines */}
        <div className="absolute inset-0 flex flex-col justify-between pointer-events-none">
          {[0, 1, 2, 3, 4].map(i => {
            const yValue = maxValue - (i * range / 4);
            return (
              <div key={i} className="border-t border-gray-100 w-full relative">
                <span className="absolute -left-12 -top-2 text-[9px] sm:text-[10px] text-gray-500 font-medium">
                  ${formatNumber(yValue)}
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Zero line if needed */}
        {minValue < 0 && maxValue > 0 && (
          <div 
            className="absolute w-full border-t-2 border-gray-300 border-dashed"
            style={{ top: `${(maxValue / range) * 100}%` }}
          />
        )}
        
        {/* SVG Line Chart */}
        <svg 
          viewBox={`0 0 100 ${chartHeight}`} 
          preserveAspectRatio="none"
          className="absolute inset-0 w-full h-full"
        >
          {/* Area fill - always blue */}
          <defs>
            <linearGradient id="areaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
            </linearGradient>
          </defs>
          <path d={areaPath} fill="url(#areaGradient)" />
          
          {/* Line - always blue */}
          <path 
            d={pathData} 
            fill="none" 
            stroke="#3b82f6"
            strokeWidth="2.5"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      
      {/* X-axis labels - Show only first, last, and a few in between */}
      <div className="flex justify-between mt-2 px-1">
        {chartData.points.map((point, index) => {
          const isFirst = index === 0;
          const isLast = index === chartData.points.length - 1;
          const showLabel = isFirst || isLast || (index % Math.ceil(chartData.points.length / 6) === 0);
          
          return showLabel ? (
            <span key={`lbl-${point.label}-${index}`} className="text-[9px] sm:text-[10px] text-gray-500 font-medium whitespace-nowrap">
              {point.label}
            </span>
          ) : (
            <span key={`sp-${point.label}-${index}`} className="flex-1"></span>
          );
        })}
      </div>
      </div> {/* Close pl-10 container */}

      {/* Summary with positive/negative trades */}
      <div className="grid grid-cols-4 gap-3 mt-6 pt-4 border-t border-gray-100">
        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase font-bold mb-1">
            {language === 'es' ? 'Total Trades' : 'Total Trades'}
          </p>
          <p className="text-2xl font-black text-gray-900">
            {bitacoraEntries.length + estudioEntries.length}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-emerald-600 uppercase font-bold mb-1">
            {language === 'es' ? 'Positivos' : 'Positive'}
          </p>
          <p className="text-2xl font-black text-emerald-600">
            {bitacoraEntries.length}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-red-600 uppercase font-bold mb-1">
            {language === 'es' ? 'Negativos' : 'Negative'}
          </p>
          <p className="text-2xl font-black text-red-600">
            {estudioEntries.length}
          </p>
        </div>
        <div className="text-center">
          <p className="text-xs text-gray-500 uppercase font-bold mb-1">Win Rate</p>
          <p className="text-2xl font-black text-indigo-600">
            {bitacoraEntries.length + estudioEntries.length > 0 
              ? ((bitacoraEntries.length / (bitacoraEntries.length + estudioEntries.length)) * 100).toFixed(0)
              : 0}%
          </p>
        </div>
      </div>
    </div>
  );
}

function TradingStreak({ bitacoraEntries, estudioEntries }) {
  const { language } = useLanguage();
  
  const streak = useMemo(() => {
    const parseDay = (s) => {
      if (!s) return null;
      let d;
      const key = normalizeDateKey(s);
      if (!key) return;
      const [y, m, day] = key.split('-');
      d = new Date(+y, +m - 1, +day);
      return isNaN(d.getTime()) ? null : d.toDateString();
    };
    
    const daySet = new Set(
      [...bitacoraEntries, ...estudioEntries].map(e => parseDay(e.date)).filter(Boolean)
    );
    if (daySet.size === 0) return 0;
    
    const prevBusinessDay = (date) => {
      const d = new Date(date);
      do { d.setDate(d.getDate() - 1); } while (d.getDay() === 0 || d.getDay() === 6);
      return d;
    };
    
    let cursor = new Date();
    cursor.setHours(0, 0, 0, 0);
    // Streak is alive if traded today or on the last business day
    if (!daySet.has(cursor.toDateString())) {
      const prev = prevBusinessDay(cursor);
      if (!daySet.has(prev.toDateString())) return 0;
      cursor = prev;
    }
    
    let count = 0;
    while (daySet.has(cursor.toDateString())) {
      count++;
      cursor = prevBusinessDay(cursor);
    }
    return count;
  }, [bitacoraEntries, estudioEntries]);
  
  return (
    <div 
      className={`p-4 sm:p-5 rounded-xl shadow-sm border flex items-center gap-3 ${
        streak > 0 
          ? 'bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200' 
          : 'bg-white border-gray-100'
      }`} 
      data-testid="trading-streak-card"
    >
      <div className={`p-2.5 rounded-full shrink-0 ${streak > 0 ? 'bg-orange-100' : 'bg-gray-50'}`}>
        <Flame size={22} className={streak > 0 ? 'text-orange-500' : 'text-gray-400'} />
      </div>
      <div>
        <p className={`font-black text-sm sm:text-base ${streak > 0 ? 'text-orange-700' : 'text-gray-500'}`} data-testid="trading-streak-count">
          {streak > 0 
            ? (language === 'es' 
                ? `🔥 Racha de ${streak} día${streak > 1 ? 's' : ''}` 
                : `🔥 ${streak}-day streak`)
            : (language === 'es' ? 'Sin racha activa' : 'No active streak')}
        </p>
        <p className={`text-xs sm:text-sm ${streak > 0 ? 'text-orange-600/80' : 'text-gray-400'}`}>
          {streak > 0 
            ? (language === 'es' ? 'Días hábiles consecutivos operando. ¡Sigue así!' : 'Consecutive business days trading. Keep it up!')
            : (language === 'es' ? 'Registra un trade hoy para iniciar tu racha' : 'Log a trade today to start your streak')}
        </p>
      </div>
    </div>
  );
}

function ExtractionProgress({ bitacoraEntries, estudioEntries, percentage, initialCapital, confirmedExtractions, startingBalance, onNavigate }) {
  const { language } = useLanguage();
  
  const stats = useMemo(() => {
    // Shared Plan logic: streak-based cuts with loss cancellation
    const rows = computeExecutedRows({
      dayGroups: buildDayGroups(bitacoraEntries, estudioEntries),
      percentage,
      initialCapital,
      confirmedExtractions,
      startingBalance
    });
    const total = rows.length;
    const lastStreak = total > 0 ? rows[total - 1].streak : 0;
    const progress = lastStreak % 5;
    const remaining = 5 - progress;
    
    const pendingRows = rows.filter(r => r.extraction > 0 && !confirmedExtractions[r.index]);
    const pendingCount = pendingRows.length;
    const pendingTotal = pendingRows.reduce((sum, r) => sum + r.extraction, 0);
    
    return { total, progress, remaining, pendingCount, pendingTotal };
  }, [bitacoraEntries, estudioEntries, percentage, initialCapital, confirmedExtractions, startingBalance]);
  
  if (stats.pendingCount > 0) {
    return (
      <button
        onClick={() => onNavigate('plan')}
        className="w-full bg-gradient-to-r from-indigo-600 to-emerald-600 text-white p-4 sm:p-5 rounded-xl shadow-lg flex items-center justify-between gap-3 hover:shadow-xl transition-all transform hover:scale-[1.01] active:scale-95 text-left"
        data-testid="home-pending-extraction-card"
      >
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2.5 rounded-full shrink-0">
            <Coins size={22} />
          </div>
          <div>
            <p className="font-black text-sm sm:text-base">
              💰 {language === 'es' 
                ? `${stats.pendingCount} retiro${stats.pendingCount > 1 ? 's' : ''} pendiente${stats.pendingCount > 1 ? 's' : ''} por $${formatNumber(stats.pendingTotal)}` 
                : `${stats.pendingCount} pending withdrawal${stats.pendingCount > 1 ? 's' : ''} of $${formatNumber(stats.pendingTotal)}`}
            </p>
            <p className="text-white/80 text-xs sm:text-sm">
              {language === 'es' ? 'Confírmalos en el Plan cuando retires el dinero' : 'Confirm them in the Plan when you withdraw the money'}
            </p>
          </div>
        </div>
        <ArrowRight size={20} className="shrink-0" />
      </button>
    );
  }
  
  return (
    <div className="bg-white p-4 sm:p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="home-extraction-progress-card">
      <div className="flex items-center gap-3">
        <div className="bg-indigo-50 p-2.5 rounded-full shrink-0">
          <Target size={22} className="text-indigo-600" />
        </div>
        <div>
          <p className="font-bold text-sm sm:text-base text-gray-900">
            {language === 'es' 
              ? `${stats.remaining === 1 ? 'Falta' : 'Faltan'} ${stats.remaining} trade${stats.remaining > 1 ? 's' : ''} para tu próxima extracción` 
              : `${stats.remaining} trade${stats.remaining > 1 ? 's' : ''} left until your next extraction`}
          </p>
          <p className="text-gray-500 text-xs sm:text-sm">
            {language === 'es' ? 'Cada 5 trades positivos generas una extracción' : 'Every 5 positive trades generates an extraction'}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-1.5 pl-12 sm:pl-0" data-testid="extraction-progress-dots">
        {[0, 1, 2, 3, 4].map(i => (
          <span 
            key={i} 
            className={`w-3 h-3 rounded-full transition-colors ${i < stats.progress ? 'bg-indigo-600' : 'bg-gray-200'}`}
          />
        ))}
        <span className="ml-2 text-xs font-bold text-indigo-600">{stats.progress}/5</span>
      </div>
    </div>
  );
}

export function Home({ onNavigate, onNewEntry, user, bitacoraEntries = [], estudioEntries = [], percentage = 10, initialCapital = 0, extraDeposits = [], confirmedExtractions = {}, accountAdjustment = 0 }) {
  const { t, language } = useLanguage();
  
  return (
    <div className="max-w-5xl mx-auto space-y-6 sm:space-y-8">
      <div className="text-center space-y-2">
        <h1 className="text-2xl sm:text-3xl md:text-4xl font-black text-blue-900 tracking-tight">{t.auth.title}</h1>
        <p className="text-amber-500 font-medium text-sm sm:text-base px-4">
          {user.isAdmin ? t.home.welcomeAdmin : t.home.welcomeUser}
        </p>
      </div>

      {/* Streak + Extraction Progress Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TradingStreak bitacoraEntries={bitacoraEntries} estudioEntries={estudioEntries} />
        <ExtractionProgress 
          bitacoraEntries={bitacoraEntries} 
          estudioEntries={estudioEntries}
          percentage={percentage} 
          initialCapital={initialCapital}
          confirmedExtractions={confirmedExtractions} 
          startingBalance={initialCapital + (extraDeposits || []).reduce((s, d) => s + (d.amount || 0), 0) + (accountAdjustment || 0)}
          onNavigate={onNavigate} 
        />
      </div>

      {/* Progress Chart */}
      <ProgressChart 
        bitacoraEntries={bitacoraEntries} 
        estudioEntries={estudioEntries} 
        percentage={percentage}
        initialCapital={initialCapital}
        totalDeposits={(extraDeposits || []).reduce((s, d) => s + (d.amount || 0), 0)}
        confirmedExtractions={confirmedExtractions}
        accountAdjustment={accountAdjustment}
      />

      {/* Main Action */}
      <button 
        onClick={onNewEntry}
        data-testid="new-entry-btn"
        className="w-full bg-blue-950 hover:bg-blue-900 text-white p-6 sm:p-8 rounded-2xl shadow-xl transition-all transform hover:scale-[1.02] flex flex-col items-center justify-center gap-3 sm:gap-4 group border border-white/10"
      >
        <div className="bg-white/10 p-3 sm:p-4 rounded-full group-hover:rotate-90 transition-transform duration-300 border border-white/10">
          <Plus size={36} strokeWidth={3} className="sm:w-12 sm:h-12" />
        </div>
        <div className="text-center">
          <h3 className="text-xl sm:text-2xl font-black tracking-wider uppercase">{t.home.newEntry}</h3>
          <p className="text-white/70 font-medium text-sm sm:text-base">{t.home.newEntryDesc}</p>
        </div>
      </button>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Calculators */}
        <div className="space-y-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-700 uppercase tracking-widest flex items-center gap-2">
            <CalcIcon size={20} className="text-blue-500" /> {t.home.calculators}
          </h2>
          <div className="grid grid-cols-1 gap-4">
            <HomeCard 
              onClick={() => onNavigate('calc')} 
              icon={<CalcIcon className="text-emerald-500" size={22} />} 
              title={t.home.limitTitle} 
              description={t.home.limitDesc}
            />
            <HomeCard 
              onClick={() => onNavigate('contracts')} 
              icon={<Coins className="text-amber-500" size={22} />} 
              title={t.home.contractsTitle} 
              description={t.home.contractsDesc}
            />
            <HomeCard 
              onClick={() => onNavigate('practica')} 
              icon={<CalendarIcon className="text-emerald-500" size={22} />} 
              title={t.home.practiceTitle} 
              description={t.home.practiceDesc}
            />
          </div>
        </div>

        {/* Databases & Admin */}
        <div className="space-y-4">
          <h2 className="text-base sm:text-lg font-bold text-gray-700 uppercase tracking-widest flex items-center gap-2">
            <Database size={20} className="text-indigo-500" /> 
            {user.isAdmin ? t.home.adminAndData : t.home.databases}
          </h2>
          <div className="grid grid-cols-1 gap-4">
            {user.isAdmin && (
              <HomeCard 
                onClick={() => onNavigate('admin')} 
                icon={<Database className="text-blue-600" size={22} />} 
                title={t.home.adminTitle} 
                description={t.home.adminDesc}
                variant="admin"
              />
            )}
            <HomeCard 
              onClick={() => onNavigate('bitacora')} 
              icon={<FileText className="text-blue-500" size={22} />} 
              title={t.home.bitacoraTitle} 
              description={t.home.bitacoraDesc}
            />
            <HomeCard 
              onClick={() => onNavigate('estudio')} 
              icon={<BookOpen className="text-red-500" size={22} />} 
              title={t.home.estudioTitle} 
              description={t.home.estudioDesc}
            />
            <HomeCard 
              onClick={() => onNavigate('plan')} 
              icon={<Target className="text-indigo-500" size={22} />} 
              title={t.home.planTitle} 
              description={t.home.planDesc}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
